import { useState, useMemo, useEffect } from 'react';
import type { Device, SleepData, UserShift, Group } from '../types';
import { api } from '../utils/api';
import { numericLocaleCompare } from '../utils/sortUtils';
import moment from 'moment-timezone';

interface DeviceListProps {
  devices: Device[];
  sleepData?: SleepData[];
  scheduleData?: UserShift[];
  className?: string;
  onDeviceSelect?: (deviceId: string) => void;
}

type FilterOption = 'all' | 'normal' | 'abnormal' | 'nodata';
type ShiftFilterOption = 'all' | 'day' | 'night' | 'other';

export default function DeviceList({ devices: initialDevices, className, onDeviceSelect }: DeviceListProps) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [activeDeviceId, setActiveDeviceId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOption, setFilterOption] = useState<FilterOption>('all');
  const [shiftFilterOption, setShiftFilterOption] = useState<ShiftFilterOption>('all');
  const [sleepData, setSleepData] = useState<Map<string, SleepData>>(new Map());
  const [scheduleData, setScheduleData] = useState<Map<string, string>>(new Map());
  const [selectedDate, setSelectedDate] = useState<string>(moment().tz('Asia/Jakarta').format('YYYY-MM-DD')); // Use Indonesia timezone
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [filteredSleepCount, setFilteredSleepCount] = useState({
    normal: 0,
    abnormal: 0,
    nodata: 0,
    total: 0,
  });

  const [filteredShiftCount, setFilteredShiftCount] = useState({
    day: 0,
    night: 0,
    other: 0,
    total: 0,
  });

  // Use initialDevices directly instead of storing in state
  const devices = initialDevices;

  // Debug: Log devices when they change
  useEffect(() => {
    console.log('DeviceList - Devices received:', devices.length, 'selectedDate:', selectedDate);
  }, [devices, selectedDate]);

  // Fetch companies and groups on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const groupsData = await api.getAllGroups();
        setGroups(groupsData);
      } catch (error) {
        console.error('Error fetching groups:', error);
      }
    };
    fetchData();
  }, []);

  // Sort devices by idEmployee first, then by name
  const sortedDevices = useMemo(() => {
    return [...devices].sort((a, b) => {
      if (a.idEmployee && b.idEmployee) {
        return numericLocaleCompare(a.idEmployee, b.idEmployee);
      }
      if (a.idEmployee && !b.idEmployee) return -1;
      if (!a.idEmployee && b.idEmployee) return 1;
      
      const nameA = (a.employee?.Nama || a.name || 'Unnamed').toLowerCase();
      const nameB = (b.employee?.Nama || b.name || 'Unnamed').toLowerCase();
      return numericLocaleCompare(nameA, nameB);
    });
  }, [devices]);

  // Auto-select first device after data is loaded
  useEffect(() => {
    // Only auto-select if:
    // 1. We have devices
    // 2. No device is currently selected
    // 3. Data has finished loading
    // 4. We have sleep data (fetch completed successfully)
    if (sortedDevices.length > 0 && !activeDeviceId && !isLoading && sleepData.size > 0) {
      const firstDevice = sortedDevices[0];
      setActiveDeviceId(firstDevice.id);
      
      const shiftType = scheduleData.get(firstDevice.id) || null;
      
      console.log('DeviceList - Auto-selecting first device after data loaded:', firstDevice.id, 'sleepData size:', sleepData.size);
      
      // Small delay to ensure state is updated
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('device-select', {
            detail: { deviceId: firstDevice.id, shiftType },
          }),
        );
      }, 100);
    }
  }, [sortedDevices, activeDeviceId, isLoading, sleepData, scheduleData]);

  // Fetch sleep and schedule data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        if (devices.length === 0) {
          console.log('DeviceList - No devices, skipping fetch');
          setIsLoading(false);
          return;
        }

        console.log('DeviceList - Fetching data for date:', selectedDate, 'devices:', devices.length);

        // Fetch user shifts separately like Dashboard does
        const [summaryResponse, shiftsResponse] = await Promise.all([
          api.getSummaryHealth({
            date: selectedDate,
            limit: 100
          }),
          api.getUserShiftsByDate(selectedDate)
        ]);

        console.log('DeviceList - Fetch completed, summary devices:', summaryResponse.data?.devices?.length);

        // Process shifts data first
        const scheduleMap = new Map<string, string>();
        if (shiftsResponse && shiftsResponse.all) {
          shiftsResponse.all.forEach((shift: UserShift) => {
            // Find device with matching idEmployee
            if (shift.idEmployee) {
              const matchingDevice = devices.find(d => d.idEmployee === shift.idEmployee);
              if (matchingDevice) {
                // Store schedule_type directly as string, like Dashboard does
                scheduleMap.set(matchingDevice.id, shift.schedule_type);
              }
            }
          });
        }

        console.log('DeviceList - Schedule map size:', scheduleMap.size);

        if (summaryResponse.success && summaryResponse.data) {
          const sleepMap = new Map<string, SleepData>();
          
          let normal = 0, abnormal = 0, nodata = 0;
          const shiftCounts = { day: 0, night: 0, other: 0, total: 0 };
          const minSleepInSeconds = 21600; // 6 hours
          
          console.log('DeviceList - Processing', summaryResponse.data.devices.length, 'devices from API');
          console.log('DeviceList - Local devices count:', devices.length);
          
          // Create a map of API data for quick lookup
          const apiDataMap = new Map();
          summaryResponse.data.devices.forEach((deviceSummary: any) => {
            apiDataMap.set(deviceSummary.deviceId, deviceSummary);
          });
          
          // Process ALL local devices, not just API devices
          devices.forEach((device) => {
            // Skip devices without employee
            if (!device.idEmployee) {
              return;
            }
            
            const deviceSummary = apiDataMap.get(device.id);
            const sleepTime = deviceSummary?.sleepData?.sleepTime || 0;
            
            // ALWAYS add device to sleepMap
            sleepMap.set(device.id, {
              deviceId: device.id,
              device_id: device.id,
              date: selectedDate,
              sleepTime: sleepTime,
              sleepQuality: deviceSummary?.sleepData?.sleepQuality ? deviceSummary.sleepData.sleepQuality / 100 : 0,
              sleepTotalTime: sleepTime,
              originalSleepTime: sleepTime,
              heartRate: deviceSummary?.sleepData?.heartRate || { min: 0, max: 0 },
              bloodOxygen: deviceSummary?.sleepData?.bloodOxygen || { min: 0, max: 0 },
              sleepLogs: [],
              startTime: deviceSummary?.sleepData?.startTime,
              endTime: deviceSummary?.sleepData?.endTime
            } as SleepData);

            // Count shifts from scheduleMap
            const shift = scheduleMap.get(device.id);
            if (shift) {
              if (shift === 'day') shiftCounts.day++;
              else if (shift === 'night') shiftCounts.night++;
              else shiftCounts.other++;
            } else {
              shiftCounts.other++;
            }
            shiftCounts.total++;

            // Count sleep status
            if (sleepTime === 0) {
              nodata++;
            } else if (sleepTime >= minSleepInSeconds) {
              normal++;
            } else {
              abnormal++;
            }
          });

          console.log('DeviceList - Setting sleep data, map size:', sleepMap.size);
          console.log('DeviceList - Sleep counts - normal:', normal, 'abnormal:', abnormal, 'nodata:', nodata);

          // Set data in correct order to avoid race conditions
          setSleepData(sleepMap);
          setScheduleData(scheduleMap);
          setFilteredSleepCount({
            normal,
            abnormal,
            nodata,
            total: devices.filter(d => d.idEmployee).length
          });
          setFilteredShiftCount(shiftCounts);
          
          console.log('DeviceList - Data set complete, sleepMap keys:', Array.from(sleepMap.keys()).slice(0, 3));
        }
      } catch (error) {
        console.error('DeviceList - Error fetching data:', error);
      } finally {
        setIsLoading(false);
        console.log('DeviceList - Loading complete');
      }
    };

    if (devices.length > 0) {
      console.log('DeviceList - Starting fetch for', devices.length, 'devices');
      fetchData();
    } else {
      console.log('DeviceList - No devices to fetch');
    }
  }, [devices, selectedDate]);

  // Listen for date changes and re-trigger device selection
  useEffect(() => {
    const handleDateChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      let formattedDate: string;
      
      if (typeof customEvent.detail === 'string') {
        formattedDate = customEvent.detail;
      } else if (customEvent.detail instanceof Date) {
        // Use Indonesia timezone for consistency
        formattedDate = moment(customEvent.detail).tz('Asia/Jakarta').format('YYYY-MM-DD');
      } else {
        formattedDate = moment().tz('Asia/Jakarta').format('YYYY-MM-DD');
      }
      
      console.log('DeviceList - Date changed to:', formattedDate);
      setSelectedDate(formattedDate);
    };

    document.addEventListener('datepicker-range-end', handleDateChange as EventListener);
    document.addEventListener('datepicker-change', handleDateChange as EventListener);
    return () => {
      document.removeEventListener('datepicker-range-end', handleDateChange as EventListener);
      document.removeEventListener('datepicker-change', handleDateChange as EventListener);
    };
  }, []);

  // Re-trigger device selection when date changes and data is loaded
  useEffect(() => {
    // Only re-trigger if we have an active device and data has finished loading
    if (activeDeviceId && !isLoading) {
      const shiftType = scheduleData.get(activeDeviceId) || null;
      
      console.log('DeviceList - Re-triggering device select after date change:', activeDeviceId);
      // Re-dispatch device-select event with updated data
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('device-select', {
            detail: { deviceId: activeDeviceId, shiftType },
          }),
        );
      }, 100);
    }
  }, [selectedDate, isLoading]);

  // Filter devices based on search, group, shift, and sleep status
  const filteredDevices = useMemo(() => {
    return sortedDevices.filter((device) => {
      // IMPORTANT: Only show devices that are bound to employees
      if (!device.idEmployee) {
        return false;
      }

      // Search filter - allow empty search to show all
      if (searchQuery.trim()) {
        const matchesSearch =
          device.employee?.Nama?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          device.employee?.NIP?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          device.idEmployee?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          device.name?.toLowerCase().includes(searchQuery.toLowerCase());

        if (!matchesSearch) return false;
      }

      // Group filter
      if (selectedGroupId && device.group_id !== selectedGroupId) {
        return false;
      }

      // Shift filter
      if (shiftFilterOption !== 'all') {
        const scheduleType = scheduleData.get(device.id);
        
        if (shiftFilterOption === 'day' && scheduleType !== 'day') return false;
        if (shiftFilterOption === 'night' && scheduleType !== 'night') return false;
        if (shiftFilterOption === 'other' && (scheduleType === 'day' || scheduleType === 'night')) return false;
      }

      // Sleep filter
      if (filterOption !== 'all') {
        const deviceSleepData = sleepData.get(device.id);
        const sleepTime = deviceSleepData?.sleepTime || 0;

        if (filterOption === 'nodata' && sleepTime !== 0) return false;
        if (filterOption === 'normal' && sleepTime < 21600) return false;
        if (filterOption === 'abnormal' && (sleepTime === 0 || sleepTime >= 21600)) return false;
      }

      return true;
    });
  }, [sortedDevices, searchQuery, selectedGroupId, shiftFilterOption, filterOption, sleepData, scheduleData]);

  const handleDeviceClick = (deviceId: string) => {
    setActiveDeviceId(deviceId);
    
    const shiftType = scheduleData.get(deviceId) || null;
    
    if (onDeviceSelect) {
      onDeviceSelect(deviceId);
    }
    
    document.dispatchEvent(
      new CustomEvent('device-select', {
        detail: { deviceId, shiftType },
      }),
    );
  };

  const renderScheduleBadge = (scheduleType: string | undefined) => {
    if (!scheduleType) {
      return (
        <span className="inline-block px-2 py-1 text-xs rounded font-medium bg-gray-100 text-gray-600">
          -
        </span>
      );
    }

    const config: Record<string, { label: string; bgColor: string; textColor: string }> = {
      fullday: { label: 'Full Day', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
      day: { label: 'Day Shift', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
      night: { label: 'Night Shift', bgColor: 'bg-purple-100', textColor: 'text-purple-800' },
      off: { label: 'Off', bgColor: 'bg-gray-100', textColor: 'text-gray-800' }
    };

    const cfg = config[scheduleType.toLowerCase()];

    if (!cfg) {
      console.warn('DeviceList - Unknown schedule type:', scheduleType);
      return (
        <span className="inline-block px-2 py-1 text-xs rounded font-medium bg-gray-100 text-gray-600">
          {scheduleType}
        </span>
      );
    }

    return (
      <span className={`inline-block px-2 py-1 text-xs rounded font-medium ${cfg.bgColor} ${cfg.textColor}`}>
        {cfg.label}
      </span>
    );
  };

  const renderSleepTime = (deviceSleepData: SleepData | undefined, scheduleType: string | undefined) => {
    if (!deviceSleepData || deviceSleepData.sleepTime <= 0) {
      return null;
    }

    if (scheduleType === 'off') {
      return 'Off Day';
    }

    const hours = Math.floor(deviceSleepData.sleepTime / 3600);
    const mins = Math.round((deviceSleepData.sleepTime % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  return (
    <div className={`bg-white rounded-lg p-4 lg:p-6 ${className}`}>
      <h3 className='text-base lg:text-lg font-semibold mb-3 lg:mb-4'>List Employee</h3>

      {/* Search and Filter Controls */}
      <div className='space-y-2 lg:space-y-3 mb-4 lg:mb-6'>
        {/* Search Input */}
        <div className='relative'>
          <input
            type='text'
            placeholder='Search by name, NIP, or ID...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className='w-full px-3 lg:px-4 py-2 text-sm lg:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          />
          <svg
            className='absolute right-3 top-2.5 h-5 w-5 text-gray-400'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
            />
          </svg>
        </div>

        {/* Filter Options - 3 Columns Layout */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
          {/* Group Filter */}
          <div>
            <label htmlFor='group-filter' className='block text-sm font-medium text-gray-700 mb-2'>
              Filter Group
            </label>
            <select
              id='group-filter'
              value={selectedGroupId || ''}
              onChange={(e) => setSelectedGroupId(e.target.value || null)}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            >
              <option value=''>All Groups</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>

          {/* Shift Filter */}
          <div>
            <label htmlFor='shift-filter' className='block text-sm font-medium text-gray-700 mb-2'>
              Filter Shift
            </label>
            <select
              id='shift-filter'
              value={shiftFilterOption}
              onChange={(e) => setShiftFilterOption(e.target.value as ShiftFilterOption)}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            >
              <option value='all'>All Shifts</option>
              <option value='day'>Pagi ({filteredShiftCount.day})</option>
              <option value='night'>Malam ({filteredShiftCount.night})</option>
              <option value='other'>Other ({filteredShiftCount.other})</option>
            </select>
          </div>

          {/* Sleep Filter */}
          <div>
            <label htmlFor='sleep-filter' className='block text-sm font-medium text-gray-700 mb-2'>
              Filter Sleep
            </label>
            <select
              id='sleep-filter'
              value={filterOption}
              onChange={(e) => setFilterOption(e.target.value as FilterOption)}
              className='w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            >
              <option value='all'>All Sleep Status</option>
              <option value='normal'>Normal Sleep ({filteredSleepCount.normal})</option>
              <option value='abnormal'>Abnormal Sleep ({filteredSleepCount.abnormal})</option>
              <option value='nodata'>No Data Sleep ({filteredSleepCount.nodata})</option>
            </select>
          </div>
        </div>
      </div>

      {/* Employee List */}
      <div className='space-y-3 lg:space-y-4 max-h-[400px] lg:max-h-[500px] overflow-y-auto'>
        {isLoading ? (
          <div className='text-center py-8 text-gray-500'>Loading employees...</div>
        ) : filteredDevices.length === 0 ? (
          <div className='text-center py-8 text-gray-500'>
            <div>No employees found matching your criteria</div>
            <div className='text-xs text-gray-400 mt-2'>
              Total devices: {devices.length}, Filtered: {filteredDevices.length}
            </div>
            <div className='text-xs text-gray-400'>
              Devices with employee: {devices.filter(d => d.employee || d.idEmployee).length}
            </div>
          </div>
        ) : (
          filteredDevices.map((device) => {
            const deviceSleepData = sleepData.get(device.id);
            const scheduleType = scheduleData.get(device.id);
            const sleepTime = deviceSleepData?.sleepTime || 0;
            const isAbnormal = sleepTime > 0 && sleepTime < 21600;

            // Debug: Log first device to see if data is found
            if (device === filteredDevices[0]) {
              console.log('DeviceList RENDER - First filtered device ID:', device.id);
              console.log('DeviceList RENDER - Sleep data found:', deviceSleepData);
              console.log('DeviceList RENDER - Sleep time:', sleepTime);
              console.log('DeviceList RENDER - Schedule type found:', scheduleType);
              console.log('DeviceList RENDER - Sleep map size:', sleepData.size);
              console.log('DeviceList RENDER - Is loading:', isLoading);
            }

            return (
              <div
                key={device.id}
                data-device-id={device.id}
                onClick={() => handleDeviceClick(device.id)}
                className={`p-3 lg:p-4 rounded-lg cursor-pointer transition-colors
                  ${
                    activeDeviceId === device.id
                      ? 'bg-blue-50 border-2 border-blue-500'
                      : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                  } ${isAbnormal ? 'border-red-200' : ''}`}
              >
                <div className='flex items-center justify-between'>
                  <div className='flex-1'>
                    <div className='flex items-center gap-2 mb-1'>
                      <h4 className='text-sm lg:text-base font-medium'>{device.employee?.Nama || device.name || 'Unnamed'}</h4>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        device.group?.name 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {device.group?.name || 'Group not set'}
                      </span>
                    </div>
                    <p className='text-xs text-gray-600'>
                      {device.employee?.NIP || 'NIP Not Set'} - {device.employee?.Jabatan || 'Title Not Assigned'}
                    </p>
                    {sleepTime > 0 || scheduleType === 'off' ? (
                      <p className='text-xs text-gray-500 mt-1'>
                        Total Sleep Time: {renderSleepTime(deviceSleepData, scheduleType)}
                      </p>
                    ) : (
                      <p className='text-xs text-gray-500 mt-1'>Total Sleep Time: 0h 0m</p>
                    )}
                  </div>
                  <div className='flex flex-col items-end gap-2'>
                    {renderScheduleBadge(scheduleType)}
                    {sleepTime > 0 ? (
                      <span
                        className={`px-2 py-1 rounded-full text-xs
                          ${
                            sleepTime >= 21600
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {sleepTime >= 21600 ? 'Normal Sleep' : 'Abnormal Sleep'}
                      </span>
                    ) : scheduleType === 'off' ? (
                      <span className='px-2 py-1 rounded-full text-xs text-gray-800 bg-blue-100'>Off Day</span>
                    ) : (
                      <span className='px-2 py-1 rounded-full text-xs text-gray-800 bg-stone-300'>No Sleep Data</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
